import { rememberAttribution } from '@/lib/attribution-memory'
import { landingApiRequest } from '@/lib/landing-api'
import { ensureGoogleTrackingBrowserFields } from '@/lib/google-tag'
import { ensureMetaBrowserIdentifiers } from '@/lib/meta-pixel'
import {
  collectAttributionSearchFields,
  readLpTrackingSessionFromSearch,
  resolveAttributionPageUrl,
} from '@/lib/campaign-page-url'

const TRACKING_SESSION_STORAGE_KEY = 'legalprime:campaignTrackingSessionId'

export type LandingCheckoutOrigin = 'landing_offer' | 'landing_checkout'

export type LandingTrackingPayload = {
  client_session_id: string
  event_name: 'page_view' | 'initiate_checkout'
  occurred_at: string
  page_url?: string
  referrer_url?: string
  utm?: {
    source?: string
    medium?: string
    campaign?: string
    content?: string
    term?: string
    id?: string
  }
  click_ids?: Record<string, string>
  browser_ids?: {
    fbp?: string
    fbc?: string
    ga_client_id?: string
    ga_session_id?: string
  }
  query_params?: Record<string, string>
  meta?: Record<string, string | number | boolean | null>
}

function generateSessionId(): string {
  const bytes = new Uint8Array(16)
  window.crypto.getRandomValues(bytes)
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')
}

export function getOrCreateTrackingSessionId(campaignId: number): string {
  if (typeof window === 'undefined') return ''

  const storageKey = `${TRACKING_SESSION_STORAGE_KEY}:${campaignId}`
  const fromParentEmbed = readLpTrackingSessionFromSearch(window.location.search)
  if (fromParentEmbed) {
    window.localStorage.setItem(storageKey, fromParentEmbed)
    return fromParentEmbed
  }

  const existing = window.localStorage.getItem(storageKey)
  if (existing && existing.trim() !== '') return existing

  const created = generateSessionId()
  window.localStorage.setItem(storageKey, created)
  return created
}

export function buildLandingTrackingPayload(
  campaignId: number,
  eventName: LandingTrackingPayload['event_name'],
  options?: {
    checkoutOrigin?: LandingCheckoutOrigin
    meta?: LandingTrackingPayload['meta']
  }
): LandingTrackingPayload {
  const pageUrl = resolveAttributionPageUrl()
  const { queryParams, utm, clickIds } = collectAttributionSearchFields(pageUrl)
  const persisted = rememberAttribution(campaignId, utm, clickIds)
  const metaIds = ensureMetaBrowserIdentifiers()
  const googleBrowserFields = ensureGoogleTrackingBrowserFields()
  const mergedBrowserIds = { ...metaIds, ...googleBrowserFields }
  const meta: LandingTrackingPayload['meta'] = { ...(options?.meta ?? {}) }

  if (options?.checkoutOrigin) {
    meta.checkout_origin = options.checkoutOrigin
  }

  return {
    client_session_id: getOrCreateTrackingSessionId(campaignId),
    event_name: eventName,
    occurred_at: new Date().toISOString(),
    page_url: pageUrl || undefined,
    referrer_url: typeof document !== 'undefined' && document.referrer ? document.referrer : undefined,
    utm: persisted.utm,
    click_ids: persisted.clickIds,
    browser_ids: Object.keys(mergedBrowserIds).length ? mergedBrowserIds : undefined,
    query_params: Object.keys(queryParams).length ? queryParams : undefined,
    meta: Object.keys(meta).length ? meta : undefined,
  }
}

export async function trackLandingEvent(campaignId: number, payload: LandingTrackingPayload): Promise<void> {
  await landingApiRequest(`/api/public/campaigns/${campaignId}/tracking/events`, {
    method: 'POST',
    body: JSON.stringify(payload),
  })
}
